import * as React from 'react'
import { DOM, E, flow, identity, IO, IOO, O, pipe, RA, RR, s, t, T, TE, TO, v } from '@packages/prelude'
import { Autoplay, Navigation, SwiperOptions } from 'swiper'
import '../css/main.scss'
import * as domain from '@packages/domain'
import { react, swiper } from './commons'
import { ProductPreview } from './components'
import { ApiProvider } from './api'
import { RootCrashGuard } from './error'

const Swipers: RR.ReadonlyRecord<string, SwiperOptions> = {
  recommendationsSwiper: {
    direction: 'horizontal',
    modules: [Autoplay, Navigation],
    slidesPerView: 1,
    slidesPerGroup: 1,
    breakpoints: {
      768: {
        slidesPerView: 2,
        slidesPerGroup: 2
      }
    },
    loop: true,
    speed: 1000,
    autoplay: {
      delay: 5000
    },
    navigation: {
      nextEl: '.recommendation-button-next',
      prevEl: '.recommendation-button-prev'
    }
  }
}

pipe(Swipers, RR.foldMapWithIndex(s.Ord)(v.Monoid)(swiper.create))

const program = (config: domain.Api.Config.Type) => {
  const apiDeps: domain.Http.Deps = {
    config,
    fetch: window.fetch.bind(window)
  }

  const loadInitialData = (raw: string): TE.TaskEither<domain.Error.Type, domain.Api.IALoadPreviewResponse.Type> =>
    pipe(
      t.NonEmptyString.decode(raw),
      E.mapLeft(domain.Error.toValidationError),
      TE.fromEither,
      TE.chain(hash => domain.Api.IndiAudio.loadPreview({ hash })(apiDeps)),
      TE.mapLeft(err =>
        domain.Error.Type.is.HttpError(err) && err.status.code === 404
          ? domain.Error.toAudioNotFoundError(window.location.search)
          : err
      )
    )

  return pipe(
    TE.Do,
    TE.apS(
      'widgets',
      pipe(
        TE.of(document),
        TE.chainIOK(DOM.querySelectorAll('[data-widget-type="product-preview"]')),
        TE.chainOptionK(() => domain.Error.toDOMError('[data-widget-type="product-preview"]'))(identity),
        TE.chain(
          flow(
            RA.map(element =>
              pipe(
                (element as HTMLElement).dataset?.productId,
                TE.fromNullable(domain.Error.fromString('No product id found')),
                TE.chainEitherK(domain.Error.decodeWith(t.NumberFromString)),
                TE.chain(productId =>
                  pipe(
                    domain.Api.IndiAudio.loadProduct({ productId })(apiDeps),
                    TE.map(product => RR.singleton(element.id, { ...product, element }))
                  )
                )
              )
            ),
            TE.sequenceArray
          )
        )
      )
    ),
    TE.apSW(
      'initialData',
      pipe(
        new URLSearchParams(window.location.search).get('preview_id'),
        O.fromNullable,
        O.traverse(T.ApplicativePar)(loadInitialData),
        TE.fromTask
      )
    ),
    TE.chainFirstIOK(
      ({ widgets, initialData }) =>
        () =>
          pipe(
            widgets,
            RA.mapWithIndex((index, widget) =>
              pipe(
                widget,
                RR.map(({ element, ...props }) =>
                  pipe(
                    element,
                    react.mountTo(() => (
                      <RootCrashGuard>
                        <ApiProvider deps={apiDeps}>
                          <ProductPreview.View
                            {...props}
                            key={element.id}
                            config={config}
                            title={(element as HTMLElement).dataset?.title}
                            // a shared preview belongs to one product: its success restores only into the
                            // owning widget; a product-less error (bad/expired link) shows once, on the first.
                            initialData={pipe(
                              initialData,
                              O.filter(
                                E.match(
                                  () => index === 0,
                                  response => response.product === props.productId
                                )
                              )
                            )}
                          />
                        </ApiProvider>
                      </RootCrashGuard>
                    ))
                  )
                )
              )
            )
          )
    ),
    TE.chainFirstIOK(({ widgets }) =>
      pipe(
        TE.Do,
        TE.apS(
          'cta',
          pipe(
            document,
            DOM.querySelector('#cta-product-widget'),
            TE.fromIO,
            TE.chainOptionK(() => domain.Error.fromString('No cta found'))(identity)
          )
        ),
        TE.apSW('widgets', pipe(widgets, RA.chain(RR.values), TE.of)),
        TE.chainIOK(({ cta, widgets }) => () => {
          // Create observer with CTA as root
          const observer = new IntersectionObserver(
            entries => {
              // If any widget intersects with CTA area, hide the CTA
              const isAnyWidgetIntersecting = entries.some(entry => entry.isIntersecting)
              ;(cta as HTMLElement).style.setProperty('--cta-visibility', isAnyWidgetIntersecting ? 'none' : 'block')
            },
            {
              root: null,
              threshold: 0.1
            }
          )

          // Observe all product widgets
          pipe(
            widgets,
            RA.map(widget => observer.observe(widget.element))
          )
        })
      )
    )
  )
}

pipe(
  window.wpConfig,
  domain.Error.decodeWith(domain.Api.Config.Type),
  TE.fromEither,
  TE.chainW(program),
  T.chainFirstIOK(E.match(domain.Error.log, () => IO.of(undefined)))
)()

// react to click on 'add-to-cart' button to show notice
window.jQuery(document.body).on(
  'wc_fragment_refresh',
  pipe(
    TO.of({ document }),
    TO.apS(
      'formData',
      pipe(
        TO.of(new FormData()),
        TO.chainFirstIOK(fd => () => fd.append('action', 'indiaudio_add_to_cart_notice')),
        TO.chainFirstIOK(fd => () => fd.append('postNonce', window.wpConfig.postNonce))
      )
    ),
    TO.bind(
      'notice',
      TO.tryCatchK(({ formData }) =>
        fetch(window.wpConfig.ajaxUrl, {
          method: 'POST',
          body: formData
        }).then(response => response.json())
      )
    ),
    TO.chainFirstIOK(({ notice, document }) =>
      pipe(
        document,
        DOM.querySelector('.woocommerce'),
        IOO.chainFirstIOK(element => () => (element.innerHTML = notice?.data)),
        IOO.chainFirstIOK(element => () => element.scrollIntoView({ behavior: 'smooth' }))
      )
    )
  )
)
